export const INDUCTION_VERSION = 'v1.0_20260907';
export const INDUCTION_PROGRESS_KEY = 'icebergers.induction.progress';

const inductionModules = [
  {
    id: 1,
    slug: 'welcome',
    title: 'Welcome',
    durationMinutes: 4,
    blocks: [
      {
        type: 'video',
        youtubeId: '7B0lCE3iRWE',
        title: 'Welcome to the Icebergers',
        credit: 'Brighton Icebergers'
      },
      { type: 'heading', text: 'Welcome to the Brighton Icebergers!' },
      {
        type: 'paragraph',
        text: "Congratulations on joining one of Melbourne's most vibrant ocean swimming communities! The Icebergers are a passionate group of over 200 members who embrace the exhilaration and wellness benefits of cold water swimming at the Royal Brighton Yacht Club."
      },
      {
        type: 'paragraph',
        text: 'Before you take your first swim with us, this safety induction will equip you with essential knowledge to swim safely. Ocean swimming safety should always be your first priority.'
      },
      { type: 'heading', text: 'The Mental Health Benefits of Cold Water Swimming' },
      {
        type: 'paragraph',
        text: 'Cold water swimming offers remarkable mental health and wellness benefits that keep Icebergers coming back day after day.'
      },
      {
        type: 'callout',
        tone: 'info',
        title: 'Our Safety Philosophy',
        text: '"Be alert but not alarmed."'
      },
      {
        type: 'paragraph',
        text: 'The ocean is a hazardous environment at any time and ocean swimmers must always respect it. This program will help you enjoy all these wonderful benefits while swimming safely.'
      }
    ]
  },
  {
    id: 2,
    slug: 'equipment',
    title: 'Equipment Needed',
    durationMinutes: 3,
    blocks: [
      { type: 'heading', text: 'Essential Gear for Safe Ocean Swimming' },
      {
        type: 'image',
        src: '/images/module_2.jpg',
        alt: 'An open water swimmer wearing a bright yellow swim cap and goggles, with the Melbourne city skyline in the background.'
      },
      {
        type: 'paragraph',
        text: 'Having the right equipment is crucial for safe and enjoyable cold water swimming. Here is what you need:'
      },
      { type: 'heading', text: 'Visibility Equipment (Essential)' },
      {
        type: 'list',
        items: [
          {
            term: 'Fluorescent/bright swim cap',
            text: 'This is your most important safety item. A bright cap makes you visible to boats, jet skis, and fellow swimmers. Orange, pink, or yellow are ideal.'
          }
        ]
      },
      { type: 'heading', text: 'Thermal Protection' },
      {
        type: 'list',
        items: [
          {
            term: 'Two silicone swim caps',
            text: 'Wear two caps — one to keep the heat in, and one to keep the cold out. Up to 40% of heat loss occurs through the head.'
          },
          {
            term: 'Neoprene swim cap (winter)',
            text: 'Provides additional insulation for your head in very cold water.'
          },
          {
            term: 'Neoprene gloves (winter)',
            text: 'Protect your hands from cold incapacitation. Cold hands lose dexterity quickly.'
          },
          {
            term: 'Neoprene booties (winter)',
            text: 'Keep your feet covered and protected.'
          }
        ]
      },
      { type: 'heading', text: 'Post-Swim Recovery Gear' },
      {
        type: 'list',
        items: [
          { term: 'Warm hat/beanie' },
          {
            term: 'Warm layers',
            text: 'Loose-fitting clothes that are easy to put on with cold hands. Lay them out in order before your swim.'
          },
          {
            term: 'Insulating mat',
            text: 'Something to stand on while changing — not cold concrete. A foam mat or towel works well.'
          },
          {
            term: 'A hot drink',
            text: 'Warm sweet drinks help with recovery. Hot chocolate, tea with sugar, or warm water.'
          },
          {
            term: 'Goggles',
            text: 'Good quality goggles suitable for open water with a comfortable fit'
          },
          {
            term: 'Earplugs',
            text: "Help prevent surfer's ear"
          }
        ]
      },
      {
        type: 'callout',
        tone: 'tip',
        title: 'Pro Tip',
        text: 'Iceberger branded bathers, caps, and jackets are available and popular among members. They help identify fellow Icebergers in the water and at swimming events! Bathers, caps and warm RBYC Iceberger jackets can be purchased at the RBYC reception desk.'
      }
    ]
  },
  {
    id: 3,
    slug: 'cold-water-shock',
    title: 'Cold Water Shock',
    durationMinutes: 4,
    blocks: [
      { type: 'heading', text: 'Understanding Cold Water Shock' },
      {
        type: 'paragraph',
        text: 'Cold water shock is your body\'s immediate, involuntary response to sudden cold water immersion. It is the biggest danger in the first few minutes of entering cold water and can affect even experienced swimmers. Understanding it is essential for your safety.'
      },
      { type: 'heading', text: 'What Happens During Cold Water Shock (0-3 minutes)' },
      {
        type: 'paragraph',
        text: 'When your skin is suddenly cooled, your body triggers an automatic response:'
      },
      {
        type: 'list',
        items: [
          {
            term: 'Gasp reflex',
            text: 'An involuntary sharp intake of breath. This is extremely dangerous if your face is underwater — it can cause immediate drowning.'
          },
          {
            term: 'Hyperventilation',
            text: 'Rapid, uncontrollable breathing that you cannot stop. This can last 1-3 minutes.'
          },
          {
            term: 'Increased heart rate',
            text: 'Your heart rate spikes dramatically.'
          },
          {
            term: 'Blood pressure surge',
            text: 'A sharp increase that can be dangerous for those with heart conditions or high blood pressure.'
          },
          {
            term: 'Panic response',
            text: 'Fear, stress, and impaired thinking. The urge to fight or flee.'
          }
        ]
      },
      {
        type: 'callout',
        tone: 'warning',
        title: 'Important',
        text: 'Cold water shock can be just as severe at 15°C as at 5°C. Any water below 15°C can trigger this response.'
      },
      {
        type: 'linkOut',
        label: 'What influences coastal water temperatures?',
        href: 'https://media.bom.gov.au/social/blog/1407/cold-water-shock-be-prepared-before-boating/',
        source: 'Bureau of Meteorology'
      },
      { type: 'heading', text: 'How to Manage Cold Water Shock' },
      {
        type: 'list',
        items: [
          {
            term: 'NEVER jump or dive in',
            text: 'Always enter the water slowly and gradually. This is the single most important rule.'
          },
          {
            term: 'Splash water on your face and chest',
            text: 'Before full immersion, prepare your body for the cold.'
          },
          {
            term: 'Control your breathing',
            text: 'Focus on slow exhales. Breathe in for 4 counts, out for 6-8 counts. Concentrate on breathing OUT.'
          },
          {
            term: 'Wait before swimming',
            text: 'Stay in shallow water until your breathing is under control. This typically takes 60-90 seconds.'
          },
          {
            term: 'Keep your face out of the water initially',
            text: 'Until the gasp response has passed.'
          },
          {
            term: 'Float if panicked',
            text: 'If you feel panic, roll onto your back and float until the shock passes. Do not try to swim.'
          }
        ]
      },
      { type: 'heading', text: 'Who is at Higher Risk?' },
      {
        type: 'paragraph',
        text: 'Extra caution is needed if you have:'
      },
      {
        type: 'list',
        items: [
          { text: 'Heart conditions or history of heart problems' },
          { text: 'High blood pressure' },
          { text: 'Asthma (cold can trigger attacks)' }
        ]
      },
      {
        type: 'callout',
        tone: 'warning',
        text: 'If you have any of these conditions, consult your doctor before cold water swimming.'
      }
    ]
  },
  {
    id: 4,
    slug: 'acclimatisation',
    title: 'Acclimatisation',
    durationMinutes: 3,
    blocks: [
      { type: 'heading', text: 'Building Your Cold Water Tolerance' },
      {
        type: 'image',
        src: '/images/module_4.jpg',
        alt: 'Three open water swimmers spaced apart, swimming in calm water near a jetty.'
      },
      {
        type: 'paragraph',
        text: 'Acclimatisation is the process of gradually adapting your body to cold water. It takes time and regular exposure, but it allows you to swim longer and more comfortably in cold conditions.'
      },
      { type: 'heading', text: 'Key Principles of Acclimatisation' },
      {
        type: 'list',
        items: [
          {
            term: 'Start early and go gradually',
            text: 'Begin swimming in autumn and continue through winter. Your body adapts as the water temperature drops.'
          },
          {
            term: 'Frequency matters',
            text: 'Regular swims (3-4 times per week) build tolerance more effectively than occasional long swims.'
          },
          {
            term: 'Short and often',
            text: 'Multiple short swims are better than one long swim for building acclimatisation.'
          },
          {
            term: 'Consistency is key',
            text: 'Skipping long periods between swims means losing your adaptation. You will need to rebuild tolerance.'
          }
        ]
      },
      { type: 'heading', text: 'The Temperature-Time Guideline' },
      {
        type: 'paragraph',
        text: 'A general rule of thumb for new ocean swimmers (note, your tolerance will improve with experience and exposure): stay in the water for approximately the same number of minutes as the temperature in Celsius.'
      },
      {
        type: 'list',
        items: [
          { text: '10°C water = approximately 10 minutes maximum' },
          { text: '12°C water = approximately 12 minutes maximum' },
          { text: '15°C water = approximately 15 minutes maximum' }
        ]
      },
      {
        type: 'callout',
        tone: 'info',
        title: 'Note',
        text: 'This is a rough guide. Individual tolerance varies significantly. Always listen to your body.'
      },
      { type: 'heading', text: 'Building Your Tolerance — Practical Steps' },
      {
        type: 'list',
        items: [
          {
            term: 'Swim through the seasons',
            text: 'The best way to acclimatise is to "swim down the temperature" from summer through autumn into winter.'
          },
          {
            term: 'Cold showers help',
            text: 'Finishing your daily shower with 30-60 seconds of cold water helps maintain adaptation between swims.'
          },
          {
            term: 'Track your swims',
            text: 'Note water temperature and how long you swam. This helps you understand your personal limits. Water temperature is recorded on the noticeboard opposite the gym entry.'
          },
          {
            term: 'Get out wanting more',
            text: 'Always exit the water while you still feel good. The motto is: "Always get out wanting more."'
          }
        ]
      },
      { type: 'heading', text: 'Factors That Affect Your Cold Tolerance' },
      {
        type: 'paragraph',
        text: 'Your ability to handle cold varies day to day based on:'
      },
      {
        type: 'list',
        items: [
          { text: 'Sleep quality — poor sleep significantly reduces cold tolerance' },
          { text: 'Whether you have eaten recently' },
          { text: 'Hydration levels' },
          { text: 'Stress levels and general health' },
          { text: 'Body composition — lean individuals may cool faster' },
          { text: 'Age — older swimmers may need extra caution' },
          { text: 'Regularity of swims, extended absences' }
        ]
      }
    ]
  },
  {
    id: 5,
    slug: 'self-monitoring',
    title: 'Self-Monitoring',
    durationMinutes: 4,
    blocks: [
      { type: 'heading', text: 'Recognising Hypothermia in Yourself' },
      {
        type: 'image',
        src: '/images/module_5.jpg',
        alt: 'Two Icebergers members standing in the shallows after a swim, and two other members posing together in swimwear on the beach.'
      },
      {
        type: 'paragraph',
        text: 'Your ability to monitor your own condition is critical for safe cold water swimming. Hypothermia can impair your judgment, so learning to recognise early warning signs is essential.'
      },
      { type: 'heading', text: 'Pre-Swim Self-Assessment' },
      {
        type: 'paragraph',
        text: 'Before every swim, ask yourself:'
      },
      {
        type: 'list',
        items: [
          { text: 'Do I feel well today?' },
          { text: 'How did I feel after my last swim? Any lingering effects?' },
          { text: 'Have I slept well?' },
          { text: 'Have I eaten in the last 6 hours?' },
          { text: 'Am I well hydrated?' },
          { text: 'What is the water temperature today? Is it colder than my last swim?' },
          { text: 'How many minutes should I swim for in these conditions?' }
        ]
      },
      {
        type: 'callout',
        tone: 'info',
        title: 'The Golden Rule',
        text: '"If in doubt, stay out." Consider if you should swim today — the cafe is always a safe and social option!'
      },
      { type: 'heading', text: 'Warning Signs During Your Swim' },
      {
        type: 'paragraph',
        text: 'Watch for these signs that you are becoming too cold:'
      },
      {
        type: 'list',
        items: [
          {
            term: 'Uncontrollable shivering',
            text: 'Your body is trying to generate heat through muscle movement'
          },
          {
            term: 'Swimming becomes vertical',
            text: 'You struggle to maintain a horizontal position'
          },
          {
            term: 'Feeling like you are slowing down or struggling',
            text: 'Even though you are trying hard'
          },
          {
            term: 'Clenched jaw',
            text: 'Difficulty speaking freely or slurred speech'
          },
          {
            term: 'Hands become claw-like or splayed',
            text: 'Loss of fine motor control in your fingers'
          },
          {
            term: 'Confusion or unusual thoughts',
            text: 'Difficulty making decisions'
          },
          {
            term: 'Feeling unusually good (late stage)',
            text: 'Paradoxical warmth can be a danger sign'
          }
        ]
      },
      {
        type: 'callout',
        tone: 'critical',
        title: 'The Critical Rule',
        text: "If you don't feel right — GET OUT immediately. Do not wait to see if you feel better. Do not try to finish your planned distance. Exit the water immediately and begin warming up."
      },
      { type: 'heading', text: 'Trust Your Body' },
      {
        type: 'paragraph',
        text: 'Learn to listen to your body. Everyone is different, and your limits will vary day to day. The goal is to enjoy swimming safely for years to come, not to push through on any single swim.'
      },
      { type: 'heading', text: 'Further information' },
      {
        type: 'linkOut',
        label: 'DRSABCD Action Plan',
        href: 'https://stjohn.org.au/fact-sheet/drsabcd-action-plan/',
        source: 'St John Ambulance Australia'
      },
      {
        type: 'linkOut',
        label: 'Hypothermia fact sheet',
        href: 'https://stjohn.org.au/fact-sheet/hypothermia/',
        source: 'St John Ambulance Australia'
      }
    ]
  },
  {
    id: 6,
    slug: 'monitoring-others',
    title: 'Monitoring Others',
    durationMinutes: 3,
    blocks: [
      { type: 'heading', text: 'The Buddy System & Looking After Each Other' },
      {
        type: 'paragraph',
        text: 'Cold water swimming is a team activity. We look after each other. As hypothermia impairs judgment, a swimmer may not realise they are in trouble — that is where you come in.'
      },
      { type: 'heading', text: 'The Buddy System' },
      {
        type: 'paragraph',
        leadIn: 'Always swim with a friend if possible.',
        text: 'This is one of the most important safety rules.'
      },
      {
        type: 'list',
        items: [
          { text: 'Your buddy can help in an emergency' },
          { text: 'You can monitor each other for signs of hypothermia' },
          { text: 'If you cannot swim with someone, tell another member where you are going and when you will be back' },
          { text: 'Check in with each other before, during, and after the swim' }
        ]
      },
      { type: 'heading', text: 'Signs to Watch for in Others' },
      {
        type: 'paragraph',
        text: 'Watch your fellow swimmers for these warning signs:'
      },
      {
        type: 'list',
        items: [
          { term: 'Blue lips or pale face', text: 'Visible signs of cold' },
          { term: 'Unusual quietness', text: 'Someone normally chatty becoming silent' },
          { term: 'Slurred or slow speech', text: 'Difficulty forming words' },
          { term: 'Confusion or strange behaviour', text: 'Not making sense, seeming disoriented' },
          { term: 'Fumbling or clumsiness', text: 'Struggling to grip things or coordinate movements' },
          { term: 'Denial', text: 'Insisting they are fine when clearly struggling' },
          { term: 'Erratic swimming', text: 'Stroke becoming ragged or uncoordinated' }
        ]
      },
      {
        type: 'callout',
        tone: 'warning',
        title: 'Critical Understanding',
        text: 'Someone who appears completely fine getting out of the water may be in trouble 10-15 minutes later due to afterdrop. Continue watching your swim buddies during the recovery period.'
      },
      { type: 'heading', text: 'How to Help' },
      {
        type: 'list',
        items: [
          { term: 'Speak up', text: 'If you think someone is too cold, tell them. Do not assume they know.' },
          { term: 'Help them exit', text: 'Guide them to the ladder or shore' },
          { term: 'Help them dress', text: 'Someone very cold cannot dress themselves easily' },
          { term: 'Stay with them', text: 'Do not leave a cold swimmer alone' },
          { term: 'Keep asking questions', text: 'Engage them in conversation to monitor their mental state' },
          { term: 'Call for help if needed', text: 'Do not hesitate to call 000 if the situation is serious' }
        ]
      }
    ]
  },
  {
    id: 7,
    slug: 'post-swim-safety',
    title: 'Post-Swim Safety',
    durationMinutes: 4,
    blocks: [
      { type: 'heading', text: 'The Critical Post-Swim Period' },
      {
        type: 'paragraph',
        text: 'What you do immediately after leaving the water is just as important as the swim itself. This is where many swimmers make mistakes that can lead to problems.'
      },
      { type: 'heading', text: 'Understanding Afterdrop' },
      {
        type: 'paragraph',
        text: 'Afterdrop is one of the most important concepts for cold water swimmers to understand.'
      },
      {
        type: 'paragraph',
        text: 'Afterdrop is when your core body temperature continues to fall even AFTER you have left the water. This happens because:'
      },
      {
        type: 'list',
        items: [
          { text: 'Cold blood from your limbs returns to your core as circulation returns' },
          { text: 'The "cold front" continues moving inward through your body tissues' },
          { text: 'Your core temperature can continue dropping for 30-45 minutes after exiting' }
        ]
      },
      { type: 'heading', text: 'Key facts about afterdrop' },
      {
        type: 'list',
        items: [
          { text: 'You may feel fine immediately after exiting, then feel much colder 10-30 minutes later' },
          { text: 'Afterdrop can last 10-40 minutes' },
          { text: 'Everyone experiences afterdrop — it is not a sign of weakness' },
          { text: 'Afterdrop can be unpleasant with shivering, faintness, and nausea' }
        ]
      },
      { type: 'heading', text: 'Immediate Actions (First 5 Minutes)' },
      {
        type: 'list',
        items: [
          { term: 'Get dressed QUICKLY', text: 'This is not the time to chat. Get dry and dressed immediately.' },
          { term: 'Dry from top to bottom', text: 'Upper body first, then lower body' },
          { term: 'Put on your hat immediately', text: 'Significant heat loss occurs through the head' },
          { term: 'Stand on something insulating', text: 'Not cold concrete. Use a mat or towel.' },
          { term: 'Layer up', text: 'Warm, loose-fitting clothes that are easy to put on' }
        ]
      },
      {
        type: 'doDont',
        doItems: [
          { text: 'Sip warm sweet drinks (hot chocolate, tea with sugar)' },
          { text: 'Eat something — shivering uses significant energy' },
          { text: 'Move around gently (walk, do not sprint)' },
          { text: 'Have a warm shower' },
          { text: 'Use the steam room for gradual warming — with caution (DO NOT use if you are hypothermic)' },
          { text: 'Watch your swim buddy for signs of trouble' },
          { text: 'Be patient — recovery can take 30 minutes or more' }
        ],
        dontItems: [
          {
            term: 'Jump into a hot shower immediately',
            text: 'This speeds cold blood return to core, worsens afterdrop and can cause fainting'
          },
          {
            term: 'Drive or cycle home until fully recovered',
            text: 'Shivering and impaired judgment make driving dangerous'
          },
          {
            term: 'Rush the warming process',
            text: 'Gradual rewarming is safer'
          },
          {
            term: 'Stand around chatting in wet gear',
            text: 'Get dressed first, socialise later'
          }
        ]
      },
      {
        type: 'callout',
        tone: 'tip',
        title: 'Pro Tip',
        text: 'Lay out your clothes in the order you will put them on BEFORE your swim. When cold, even simple tasks become difficult.'
      },
      { type: 'heading', text: 'CPR Awareness and First Aid' },
      {
        type: 'paragraph',
        text: 'While cold water swimming with the Brighton Icebergers is a safe and rewarding activity, understanding emergency response procedures is an essential part of being a responsible community member.'
      },
      {
        type: 'paragraph',
        leadIn: 'You are not expected to be a trained lifesaver.',
        text: 'However, knowing what effective emergency response looks like helps you:'
      },
      {
        type: 'list',
        items: [
          { term: 'Recognise', text: 'when someone needs help and act quickly to call 000' },
          { term: 'Assist', text: 'trained responders if asked, rather than feeling helpless' },
          { term: 'Locate and use', text: "RBYC's defibrillators in a cardiac emergency (the AED provides voice instructions to guide you)" },
          { term: 'Understand', text: 'why our buddy system and group swimming practices exist — early intervention saves lives' }
        ]
      },
      {
        type: 'callout',
        tone: 'critical',
        title: 'Remember',
        text: 'At RBYC, defibrillators are located in the First Aid Box on the Marina AND in the hallway outside the changerooms. In any emergency, call 000 immediately and provide the club address: 253 Esplanade, Middle Brighton VIC 3186.'
      },
      { type: 'heading', text: 'Further information' },
      {
        type: 'linkOut',
        label: 'Rescuing a drowning person',
        href: 'https://stjohn.org.au/fact-sheet/rescuing-a-drowning-person/',
        source: 'St John Ambulance Australia'
      },
      {
        type: 'linkOut',
        label: 'CPR — adult or child over 1 year',
        href: 'https://stjohn.org.au/fact-sheet/cpr-adult-or-child-over-1-year/',
        source: 'St John Ambulance Australia'
      },
      {
        type: 'video',
        youtubeId: 'DKnaRQh6elg',
        title: 'Learn cardiopulmonary resuscitation (CPR) and how to use an AED',
        credit: 'Ambulance Victoria'
      }
    ]
  },
  {
    id: 8,
    slug: 'rbyc-safety',
    title: 'Closing & RBYC Safety',
    durationMinutes: 3,
    blocks: [
      { type: 'heading', text: 'RBYC Safety Equipment & Emergency Procedures' },
      {
        type: 'image',
        src: '/images/module_8.jpg',
        alt: 'A collage of Icebergers members\' beach sand art made with flip-flops, including the word "ICE" and a member bending down to draw in the sand.'
      },
      {
        type: 'paragraph',
        text: 'Every Iceberger member must know the location of all safety equipment at RBYC. Take time to familiarise yourself with these locations during your first visit.'
      },
      {
        type: 'image',
        src: '/images/rbyc-safety-locations.png',
        alt: 'Aerial photograph of the RBYC marina and beach annotated with the locations of first aid kits, defibrillators, rescue boards, rescue floats, hypothermia kits and water exit ladders. All locations are listed in the text below.',
        caption: 'Safety assistance locations noticeboard at RBYC. The written list below is the authoritative version.'
      },
      // TODO (open question OQ7, added during the §13 accessibility audit): the
      // noticeboard photograph also shows rescue floats ("opposite gym entrance"),
      // hypothermia kits x2 (male and female change rooms), and first aid kits x3
      // (RBYC, Marina, Baths) — none of which appear in the approved module text
      // below or in the source document (RBYC_Safety_Induction_V8, page 15).
      // D12 requires every location shown in the image to also exist as text.
      // Do not add wording for these from the photo alone — it is safety content
      // the RBYC Aquatic Subcommittee must approve. See specs/induction-course.md
      // §17 OQ7.
      { type: 'heading', text: 'Safety Ladders' },
      {
        type: 'list',
        items: [
          { term: 'Ladder 1', text: 'Positioned midway between the two red poles opposite the dog beach' },
          { term: 'Ladder 2', text: 'Near the attenuator pole — this location also has a rescue board' }
        ]
      },
      { type: 'heading', text: 'Rescue Boards' },
      {
        type: 'list',
        items: [
          { text: 'At Ladder 2, near the attenuator pole' },
          { text: 'On the wall of the club next to the beach exit' }
        ]
      },
      { type: 'heading', text: 'Defibrillators (AEDs)' },
      {
        type: 'list',
        items: [
          { term: 'Location 1', text: 'In the First Aid Box on the Marina' },
          { term: 'Location 2', text: 'In the hallway outside of the changerooms at RBYC' }
        ]
      },
      // TODO (open question OQ2): the noticeboard photograph shows THREE defibrillators
      // (RBYC, Marina, Baths). The approved text lists two, and assessment question Q19
      // expects two. Do not add a third location until the subcommittee confirms.
      { type: 'heading', text: 'First Aid Box' },
      {
        type: 'paragraph',
        text: 'Located inside the sliding gate to the Marina and down the ramp. Access via your membership fob. Contains: blankets, pillows, fire extinguisher, defibrillator, first aid supplies.'
      },
      {
        type: 'callout',
        tone: 'critical',
        title: 'Emergency Contact',
        text: 'Call 000 (Triple Zero). For coastal emergencies, ask for the Coastguard.'
      },
      {
        type: 'contact',
        items: [
          { label: 'Emergency', value: '000', href: 'tel:000' },
          { label: 'RBYC Address', value: 'Royal Brighton Yacht Club, 253 Esplanade, Middle Brighton, VIC 3186' },
          { label: 'Club Phone', value: '+61 3 9592 3092', href: 'tel:+61395923092' }
        ]
      },
      { type: 'heading', text: 'When to Call 000' },
      {
        type: 'list',
        items: [
          { text: 'Unconscious or unresponsive swimmer' },
          { text: 'Not breathing normally' },
          { text: 'Severe hypothermia with confusion, drowsiness, or loss of consciousness' },
          { text: 'No improvement after 30+ minutes of warming efforts' }
        ]
      },
      {
        type: 'callout',
        tone: 'warning',
        title: 'Remember',
        text: 'It is always better to call for help early than to wait.'
      },
      { type: 'heading', text: 'Summary — Key Takeaways' },
      {
        type: 'list',
        items: [
          { text: 'Cold water swimming offers tremendous mental health and wellness benefits' },
          { text: 'Proper equipment — especially visibility gear — is essential' },
          { text: 'Enter the water slowly to manage cold water shock' },
          { text: 'Build acclimatisation gradually — swim through the seasons' },
          { text: "Monitor yourself: if you don't feel right, GET OUT" },
          { text: 'Monitor others — use the buddy system' },
          { text: 'Afterdrop is real — get dressed quickly and warm up gradually' },
          { text: 'Know your safety equipment locations' }
        ]
      },
      {
        type: 'paragraph',
        text: 'Welcome to the Icebergers! We look forward to swimming with you.'
      }
    ]
  }
];

export default inductionModules;
